import { useCallback, useEffect, useRef, useState } from 'react';

import { ITEMS_VIEW_MODES } from '../utils/itemsView';

/**
 * The mode of a list that can be a grid or a table (or, on the collection
 * list, a worklist), with its storage plugged in.
 *
 *   const { mode, setMode } = useViewMode({
 *     store: localItemsViewStore(collectionId),   // { id, read(), write(mode) }
 *     url: { value: searchParams.get('view'), write: mode => ... },   // optional
 *   });
 *
 * - `store` is where the choice lives between visits. The collection list uses
 *   localStorage per collection (`localItemsViewStore`); a related list passes
 *   an account store (KPR-722) with the same `{ id, read, write }`. The `id`
 *   names what the store reads: the hook re-reads it whenever the id changes,
 *   so a new collection starts from its own stored choice. (A store built
 *   afresh every render is fine; its id is what counts.)
 * - `url`, when given, is a second home: a mode the address names wins over the
 *   stored one while it is one of `modes` (an unrecognised value falls back to
 *   the store), and `setMode` writes it there too. Other surfaces (the header's
 *   "Export view…") read what is on screen from the address.
 *
 * `setMode` keeps one identity for the hook's lifetime.
 */
export default function useViewMode({ store, url = null, modes = ITEMS_VIEW_MODES }) {
	// Read at the moment of a change, never as dependencies: react-router's
	// `setSearchParams` is a new function whenever the query string changes.
	const storeRef = useRef(store);
	storeRef.current = store;

	const [stored, setStored] = useState(() => store.read());
	useEffect(() => {
		setStored(storeRef.current.read());
	}, [store.id]);

	const urlRef = useRef(url);
	urlRef.current = url;

	const setMode = useCallback((next) => {
		setStored(next);
		storeRef.current.write(next);
		urlRef.current?.write?.(next);
	}, []);

	const fromUrl = url?.value;
	const mode = fromUrl && modes.includes(fromUrl) ? fromUrl : stored;
	return { mode, setMode };
}
